import React, { useContext, useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core';
import { AxiosResponse } from 'axios';
import {
  MdOutlineFileDownload,
  MdOutlineEdit,
  MdOutlineRestore,
  MdOutlineVisibilityOff,
} from 'react-icons/md';

import { MAIN_CONTAINER_ID } from '../../../../constants';
import { DeviceContext } from '../../../../contexts';
import { NotificationContext } from '../../../../contexts/notificationContext';
import {
  AgreementPaySchedule,
  AgreementVersion,
  AgreementVersionReason,
  AppCustomTableColumn,
  RefreshedPayrolleeDoc,
} from '../../../../types';
import {
  GetAgreementVersionsRequest,
  HideAgreementVersionRequest,
  MakeAgreementVersionCurrentRequest,
  ReissueAgreementRequest,
  UpdateAgreementVersionNoteRequest,
} from '../../../../api/types';
import { AppCustomTable, AppIconButton } from '../../../../components';
import { formatTableDate } from '../../../../utils/dates';
import { getDocumentErrorNotification } from './documentErrorNotification';
import { ReissueAgreementDialog, ReissueFormValues } from './ReissueAgreementDialog';
import { VersionActionDialog, VersionActionMode } from './VersionActionDialog';

export interface AgreementVersionRequests {
  getVersions: (data: GetAgreementVersionsRequest) => Promise<AxiosResponse<AgreementVersion[]>>;
  reissue: (data: ReissueAgreementRequest) => Promise<AxiosResponse<RefreshedPayrolleeDoc>>;
  updateNote: (data: UpdateAgreementVersionNoteRequest) => Promise<AxiosResponse<AgreementVersion>>;
  makeCurrent: (data: MakeAgreementVersionCurrentRequest) => Promise<AxiosResponse<AgreementVersion>>;
  hide: (data: HideAgreementVersionRequest) => Promise<AxiosResponse<AgreementVersion>>;
}

const REASON_LABELS: Record<AgreementVersionReason, string> = {
  initial: 'Initial',
  business_type_changed: 'Business type changed',
  entity_data_changed: 'Entity details changed',
  manual_refresh: 'Manual refresh',
  superuser_override: 'Superuser override',
  reinstated: 'Reinstated',
  backfill: 'Backfill',
};

const BUSINESS_TYPE_LABELS: Record<string, string> = {
  'sole-trader': 'Sole trader',
  company: 'Company',
  partnership: 'Partnership',
  trust: 'Trust',
};

interface Props {
  open: boolean;
  token: string;
  userId: string;
  paySchedule: AgreementPaySchedule;
  requests: AgreementVersionRequests;
  handleClose: () => void;
  // Called after a change that can move agreement_url/agreement_date on the parent row.
  onDocumentChanged: () => void;
}

export const AgreementVersionsDialog: React.FC<Props> = ({
  open,
  token,
  userId,
  paySchedule,
  requests,
  handleClose,
  onDocumentChanged,
}) => {
  const theme = useTheme();
  const { isMobile } = useContext(DeviceContext);
  const { handleServerError, showNotification } = useContext(NotificationContext);

  const [versions, setVersions] = useState<AgreementVersion[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [actionMode, setActionMode] = useState<VersionActionMode | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<AgreementVersion | null>(null);
  const [reissueOpen, setReissueOpen] = useState<boolean>(false);
  const payScheduleId = paySchedule.pay_schedule_id;

  const handleDocumentError = (e: any) => {
    const notification = getDocumentErrorNotification(e);
    if (notification) {
      showNotification(notification);
    } else {
      handleServerError(e);
    }
  };

  const fetchVersions = async () => {
    try {
      setLoading(true);
      const res = await requests.getVersions({ token, userId, payScheduleId });
      setVersions(res.data || []);
    } catch (e) {
      handleDocumentError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setVersions([]);
      fetchVersions();
    }
  }, [open, payScheduleId]);

  const closeAction = () => {
    setActionMode(null);
    setSelectedVersion(null);
  };

  const runAction = async (action: () => Promise<unknown>, refreshParent: boolean) => {
    try {
      setActionLoading(true);
      await action();
      closeAction();
      setReissueOpen(false);
      await fetchVersions();
      if (refreshParent) {
        onDocumentChanged();
      }
    } catch (e) {
      handleDocumentError(e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleActionConfirm = (text: string) => {
    if (!selectedVersion || !actionMode) {
      return;
    }
    const base = { token, userId, payScheduleId, version: selectedVersion.version };
    if (actionMode === 'edit-note') {
      runAction(() => requests.updateNote({ ...base, note: text || null }), false);
    } else if (actionMode === 'make-current') {
      runAction(() => requests.makeCurrent({ ...base, note: text || undefined }), true);
    } else {
      runAction(() => requests.hide({ ...base, reason: text }), false);
    }
  };

  const handleReissue = (values: ReissueFormValues) => {
    runAction(async () => {
      const res = await requests.reissue({
        token,
        userId,
        payScheduleId,
        businessType: values.businessType,
        agreementDate: values.agreementDate,
        note: values.note,
      });
      if (res.data?.url) {
        window.open(res.data.url, '_blank', 'noopener,noreferrer');
      }
    }, true);
  };

  const openAction = (mode: VersionActionMode, version: AgreementVersion) => {
    setSelectedVersion(version);
    setActionMode(mode);
  };

  const columns: AppCustomTableColumn[] = useMemo(
    () => [
      {
        name: 'version',
        title: 'Version',
        flex: 0.16,
        width: '130px',
        renderCell: (row: AgreementVersion) => (
          <Box display="flex" alignItems="center" gridGap={8} style={{ opacity: row.hidden_at ? 0.5 : 1 }}>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              v{row.version}
            </Typography>
            {row.is_current && (
              <Chip
                size="small"
                label="Current"
                style={{
                  backgroundColor: theme.palette.success.main,
                  color: theme.palette.success.contrastText,
                }}
              />
            )}
            {!!row.hidden_at && (
              <Tooltip title={row.hidden_reason || 'Hidden'}>
                <Chip size="small" label="Hidden" variant="outlined" />
              </Tooltip>
            )}
          </Box>
        ),
      },
      {
        name: 'agreement_date',
        title: 'Agreement Date',
        flex: 0.14,
        width: '110px',
        renderCell: (row: AgreementVersion) => (
          <Typography variant="body2" style={{ opacity: row.hidden_at ? 0.5 : 1 }}>
            {row.agreement_date ? formatTableDate(row.agreement_date) : '—'}
          </Typography>
        ),
      },
      {
        name: 'type',
        title: 'Type',
        flex: 0.16,
        width: '120px',
        renderCell: (row: AgreementVersion) => (
          <Box style={{ opacity: row.hidden_at ? 0.5 : 1 }}>
            <Typography variant="body2">
              {(row.business_type && BUSINESS_TYPE_LABELS[row.business_type]) || row.business_type || '—'}
            </Typography>
            <Typography variant="caption" style={{ color: theme.palette.text.secondary }}>
              {row.pay_scheme?.toUpperCase() || ''}
            </Typography>
          </Box>
        ),
      },
      {
        name: 'reason',
        title: 'Reason',
        flex: 0.18,
        width: '140px',
        renderCell: (row: AgreementVersion) => (
          <Typography variant="body2" style={{ opacity: row.hidden_at ? 0.5 : 1 }}>
            {REASON_LABELS[row.reason] || row.reason}
          </Typography>
        ),
      },
      {
        name: 'note',
        title: 'Note',
        flex: 0.2,
        renderCell: (row: AgreementVersion) =>
          row.note ? (
            <Tooltip title={row.note}>
              <Typography
                variant="body2"
                style={{
                  opacity: row.hidden_at ? 0.5 : 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {row.note}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="body2" style={{ color: theme.palette.text.secondary }}>
              —
            </Typography>
          ),
      },
      {
        name: 'created_at',
        title: 'Created',
        flex: 0.14,
        width: '120px',
        renderCell: (row: AgreementVersion) => (
          <Typography variant="body2" style={{ opacity: row.hidden_at ? 0.5 : 1 }}>
            {formatTableDate(row.created_at, true)}
          </Typography>
        ),
      },
      {
        name: 'actions',
        title: ' ',
        flex: 0.22,
        width: '170px',
        renderCell: (row: AgreementVersion) => (
          <Box display="flex" gridGap={4}>
            {!!row.s3_url && (
              <Tooltip title="Download PDF">
                <Box display="flex">
                  <AppIconButton
                    variant="paper"
                    onClick={() => window.open(row.s3_url as string, '_blank', 'noopener,noreferrer')}
                  >
                    <MdOutlineFileDownload color={theme.palette.secondary.main} />
                  </AppIconButton>
                </Box>
              </Tooltip>
            )}
            <Tooltip title="Edit note">
              <Box display="flex">
                <AppIconButton variant="paper" onClick={() => openAction('edit-note', row)}>
                  <MdOutlineEdit color={theme.palette.secondary.main} />
                </AppIconButton>
              </Box>
            </Tooltip>
            {!row.is_current && (
              <Tooltip title="Make this version current">
                <Box display="flex">
                  <AppIconButton variant="paper" onClick={() => openAction('make-current', row)}>
                    <MdOutlineRestore color={theme.palette.secondary.main} />
                  </AppIconButton>
                </Box>
              </Tooltip>
            )}
            {!row.hidden_at && (
              <Tooltip
                title={
                  row.is_current
                    ? "You can't hide the current version. Make another version current first."
                    : 'Hide version'
                }
              >
                <Box display="flex">
                  <AppIconButton
                    variant="paper"
                    disabled={row.is_current}
                    onClick={() => openAction('hide', row)}
                  >
                    <MdOutlineVisibilityOff
                      color={row.is_current ? theme.palette.text.disabled : theme.palette.secondary.main}
                    />
                  </AppIconButton>
                </Box>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [theme, versions]
  );

  return (
    <Dialog
      fullScreen={isMobile}
      maxWidth="lg"
      fullWidth
      container={document.getElementById(MAIN_CONTAINER_ID)}
      open={open}
      onClose={handleClose}
    >
      <DialogTitle>Agreement versions — {paySchedule.name}</DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" padding={4}>
            <CircularProgress color="secondary" />
          </Box>
        ) : versions.length ? (
          <Box paddingBottom={1}>
            <AppCustomTable columns={columns} data={versions} />
          </Box>
        ) : (
          <Box
            padding={3}
            borderRadius={8}
            border={`1px solid ${theme.palette.divider}`}
            bgcolor={theme.palette.background.paper}
          >
            <Typography variant="subtitle2">No versions yet</Typography>
            <Typography variant="caption" style={{ color: theme.palette.text.secondary }}>
              This agreement was created before versioning. Reissue it or refresh the document to start the
              history.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button size="large" style={{ minWidth: 115 }} variant="outlined" onClick={handleClose}>
          Close
        </Button>
        <Button
          size="large"
          style={{ minWidth: 160 }}
          color="primary"
          variant="contained"
          onClick={() => setReissueOpen(true)}
        >
          Reissue agreement
        </Button>
      </DialogActions>

      <ReissueAgreementDialog
        open={reissueOpen}
        loading={actionLoading}
        handleClose={() => setReissueOpen(false)}
        handleReissue={handleReissue}
      />

      <VersionActionDialog
        open={!!actionMode}
        mode={actionMode || 'edit-note'}
        version={selectedVersion}
        loading={actionLoading}
        handleClose={closeAction}
        handleConfirm={handleActionConfirm}
      />
    </Dialog>
  );
};
